import React, {
    forwardRef,
    useEffect,
    useState,
    useContext
} from "react";
import { useParams  } from "react-router-dom";
import ProfileContext from '../../../services/profileContext';
import helpers from '../../../services/helpers';
import StoreInfo from '../../parts/StoreInfo';
import initTreeView from '../../../services/initTreeView';
import { httpRequest } from '../../../services/httpService';
import Loader from '../../parts/Loader';
import store from '../../../services/store';
import Swal from 'sweetalert2';

const PosReceiptPrint = forwardRef(({
    data = {}
}, ref) => {
    const {
        account
    } = useContext(ProfileContext);
    const default_currency = helpers.getSettingValue(account?.settings, 'default_currency', '₱');
    const tax_enabled = helpers.getSettingValue(account?.settings, 'tax_enabled', 1);
    const discount_enabled = helpers.getSettingValue(account?.settings, 'discount_enabled', 1);
    const single_label = helpers.getSettingValue(account?.settings, 'invoice_single_label', 'Invoice');

    const { invoice_id } = useParams();
    const [loading, setLoading] = useState(0);
    const [record_loaded, setRecordLoaded] = useState(0);
    const [record_data, setRecordData] = useState(store.invoice_data);
    const [record_items, setRecordItems] = useState([]);
    const [table_items, setTableItems] = useState([]);

    function isObjectEmpty(obj) {
        return obj && Object.keys(obj).length === 0 && obj.constructor === Object;
        }
    
   
    useEffect(() => {
        initTreeView()

        if(isObjectEmpty(data)){
            if(invoice_id){
                setLoading(1)
                fetchData(invoice_id)
            }
        }
    }, []);

    useEffect(() => {
        if(!isObjectEmpty(data)){
            setRecordData(data)
        }
        
    }, [data]);

    function fetchData(invoice_id){
        if(!invoice_id){
            return
        }
        const url = helpers.createUrl('V1/manage/invoices/'+invoice_id+'?store_id='+helpers.getStoreID())

        httpRequest(url, "GET", {}, true)
        .then((res) => {
            let items = res;
            items.invoice_date = new Date(res.invoice_date)
            items.due_date = new Date(res.due_date)

            setLoading(0)
            setRecordData(items)
            setRecordItems(items.items)
            setRecordLoaded(1)
            
            setTimeout(function(){
                //window.print()
            }, 2000);
        })
        .catch((err) => {
            setLoading(0)
            //getEnums()

            Swal.fire({
                //title: err.message,
                text: err.message,
                icon: "error",
                confirmButtonText: "OK",
                allowOutsideClick: false
            })
            .then((result) => {
                if (result.isConfirmed) {
                    window.location.replace(`/invoices/${invoice_id}`);
                }
            })
        }); 

    }

    function computeSubTotals(){
        let items = 0;
        let total_amount = 0

        record_items.forEach((data) => {
            const quantity = parseInt(data.quantity ?? '0', 10);
            const price = parseFloat(data.amount ?? '0');
            const discount = parseFloat(data.discount_amount ?? '0');

            const totalPrice = price * quantity
            const netPrice = totalPrice - discount;

            items += quantity;
            total_amount += netPrice
        });

        return helpers.formatCurrencyAmount(total_amount, default_currency);
    }

    const formatDateTime = (date) => {

        if (!date) return "";

        const d = new Date(
            date.replace(" ", "T") + "Z"
        );

        const parts = new Intl.DateTimeFormat("en-PH", {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
            second: "2-digit",
            hour12: true
        }).formatToParts(d);

        const get = type => parts.find(p => p.type === type)?.value || "";

        return `${get("month")} ${get("day")}, ${get("year")} ${get("hour")}:${get("minute")}:${get("second")} ${get("dayPeriod")}`;
    };

    return (
        <div ref={ref} className="invoice p-4 mb-3 rounded">
            <Loader isLoading={loading}/>

            <div className='text-center'>
                <StoreInfo store={record_data.store_info} />
                <div style={{fontSize: '14px'}}>TIN: 000-000-000</div>
            </div>
            <hr/>
                <p className='mb-0 text-center'><strong>SALES INVOICES</strong></p>
            <hr/>
            <div style={{fontSize: '14px'}}>
                <div>Invoice No: {record_data.invoice_no}</div>
                <div>Date: {formatDateTime(record_data.created_at)}</div>
                <div>Cashier: {record_data?.pos_session?.opened_by_name ? record_data?.pos_session?.opened_by_name : '---'}</div>
                <div>Terminal: {record_data?.pos_session?.id ? `${record_data?.pos_session?.terminal_code} - ${record_data?.pos_session?.terminal_name}` : "--"}</div>
                <div>Session ID: {String(record_data?.pos_session?.id ?? 0).padStart(5, "0")}</div>
            </div>
            <hr/>
            <div style={{fontSize: '14px'}}>
                <div>Customer: {record_data.customer_name != 'Walk-In' ? record_data.customer_name : '---'}</div>
            </div>
            <hr/>
            <div style={{fontSize: '14px'}}>
                {record_data?.items?.map(item => (
                    <div>
                        <div>
                            {item.product_name} 
                            {item.product_variant_name !== 'Generic' ? ` (${item.product_variant_name})` : ''}
                        </div>
                        <div className='d-flex justify-content-between'>
                            <div>{item.quantity} x ₱{helpers.formatAmount(item.amount)}{item.unit_name ? `/${item.unit_name}` : ''}</div>
                            <div>₱{helpers.formatAmount(item.total_amount)}</div>
                        </div>
                    </div>
                ))}
            </div>
            <hr/>
            <div style={{fontSize: '14px'}}>
                <div className='d-flex justify-content-between'>
                    <div>Total Items:</div> 
                    <div>{record_data.total_items}</div> 
                </div>
                <div className='d-flex justify-content-between'>
                    <div>Tax: </div> 
                    <div>₱{helpers.formatAmount(record_data.total_tax)}</div> 
                </div>
                <div className='d-flex justify-content-between'>
                    <div>Discount: </div> 
                    <div>₱{helpers.formatAmount(record_data.total_discount)}</div> 
                </div>
                <div className='d-flex justify-content-between font-weight-bold'>
                    <div>TOTAL:</div> 
                    <div>₱{helpers.formatAmount(record_data.total_amount)}</div> 
                </div>
            </div>
            <hr/>
            <div className='text-center' style={{fontSize: '14px'}}>
                <div className='font-weight-bold'>BentaPro</div>
                <div style={{fontSize: '12px'}}>www.bentapro.com</div>
                <div style={{fontSize: '12px'}}>Ormoc City, Leyte PH 6541</div>
            </div>
        </div>
    )
});

export default PosReceiptPrint;